import { Component, type ErrorInfo, type ReactNode, useEffect } from "react";
import { useLocation } from "react-router";
import { reportClientDebugEvent } from "@/lib/client-observability";

type RuntimeErrorBoundaryProps = {
  children: ReactNode;
  scope: string;
  section?: boolean;
};

type RuntimeErrorBoundaryState = {
  error: Error | null;
};

export class RuntimeErrorBoundary extends Component<
  RuntimeErrorBoundaryProps,
  RuntimeErrorBoundaryState
> {
  state: RuntimeErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportClientDebugEvent({
      event: "react.render_error",
      level: "error",
      details: {
        scope: this.props.scope,
        error,
        componentStack: info.componentStack,
      },
    });
  }

  private retry = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.section) {
      return (
        <section
          role="alert"
          className="rounded-2xl border border-destructive/40 bg-destructive/5 p-5"
        >
          <h2 className="font-display text-lg font-semibold">
            Questa sezione non si è caricata
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            L’errore è stato registrato nei log tecnici. Le altre impostazioni
            restano disponibili.
          </p>
          <button
            type="button"
            onClick={this.retry}
            className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            Riprova
          </button>
        </section>
      );
    }
    return (
      <main
        role="alert"
        className="grid min-h-svh place-items-center bg-background px-6 text-foreground"
      >
        <section className="w-full max-w-xl rounded-3xl border bg-card p-8 shadow-xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">
            Enkey Agency
          </p>
          <h1 className="mt-3 font-display text-2xl font-bold">
            La pagina ha incontrato un errore
          </h1>
          <p className="mt-3 leading-7 text-muted-foreground">
            Il dettaglio è stato registrato automaticamente nella cartella dei
            log. Puoi ricaricare la pagina per riprendere il lavoro.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              Ricarica pagina
            </button>
            <button
              type="button"
              onClick={this.retry}
              className="rounded-lg border px-4 py-2.5 text-sm font-semibold"
            >
              Riprova senza ricaricare
            </button>
          </div>
        </section>
      </main>
    );
  }
}

export function RuntimeNavigationObserver() {
  const location = useLocation();

  useEffect(() => {
    reportClientDebugEvent({
      event: "route.view",
      details: { route: location.pathname },
    });
  }, [location.pathname]);

  return null;
}

export function RuntimeLoadingFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="grid min-h-[45vh] place-items-center bg-background px-6"
    >
      <div className="flex items-center gap-3 rounded-2xl border bg-card px-5 py-4 text-sm font-medium text-muted-foreground shadow-sm">
        <span className="size-4 animate-spin rounded-full border-2 border-primary/25 border-t-primary" />
        Caricamento della pagina…
      </div>
    </div>
  );
}
